import { APP_PATHS, SERVICE_REQUEST_TYPES } from '@/constant/app-paths';

/** The breadcrumb label (translation key) of each page, by its path. */
export const DYNAMIC_BREADCRUMB_TITLES: Record<string, string | null> = {
  [APP_PATHS.MY_PROFILE]: 'PROFILE',
  [APP_PATHS.MY_PROFILE_EDIT]: 'EDIT_PROFILE',
  '/404': 'ERROR_404',
  '/503': 'ERROR_503',

  // The same keys as the menu (src/constant/hr-sidebar-menu.ts), all in `generic`.
  [APP_PATHS.HOME]: 'HOME',
  [APP_PATHS.PEOPLE]: 'PEOPLE',

  [APP_PATHS.TIME_TRACKER]: 'TIME_TRACKER',
  [APP_PATHS.ATTENDANCE]: 'ATTENDANCE',
  [APP_PATHS.TEAM_ATTENDANCE]: 'TEAM_ATTENDANCE',
  [APP_PATHS.TEAM_TIMESHEETS]: 'TEAM_TIMESHEETS',

  [APP_PATHS.MY_TIME_OFF]: 'MY_TIME_OFF',
  [APP_PATHS.TEAM_TIME_OFF]: 'TEAM_TIME_OFF',
  [APP_PATHS.TIME_OFF_CALENDAR]: 'CALENDAR',
  [APP_PATHS.SICK_LEAVE]: 'SICK_LEAVE',

  [APP_PATHS.MY_REVIEWS]: 'MY_REVIEWS',
  [APP_PATHS.MY_REVIEW_SUBMISSIONS]: 'MY_SUBMISSIONS',
  [APP_PATHS.ALL_REVIEWS]: 'ALL_REVIEWS',
  [APP_PATHS.PERFORMANCE_DASHBOARD]: 'DASHBOARD',

  [APP_PATHS.CANDIDATES]: 'CANDIDATES',
  [APP_PATHS.OFFERS]: 'OFFERS',

  [APP_PATHS.ALL_SURVEYS]: 'ALL_SURVEYS',
  [APP_PATHS.MY_SURVEY_RESPONSES]: 'MY_RESPONSES',

  [APP_PATHS.ORDER_MEAL]: 'ORDER_MEAL',
  [APP_PATHS.MEAL_ORDERS]: 'MEAL_ORDERS',

  [APP_PATHS.SERVICE_REQUESTS]: 'SERVICE_REQUESTS',

  [APP_PATHS.DEPARTMENTS]: 'DEPARTMENTS',
  [APP_PATHS.DESIGNATIONS]: 'DESIGNATIONS',
  [APP_PATHS.LOCATIONS]: 'LOCATIONS',
  [APP_PATHS.PROJECTS]: 'PROJECTS',
  [APP_PATHS.MBM_PROJECTS]: 'MBM_PROJECTS',
  [APP_PATHS.LEAVE_POLICIES]: 'LEAVE_POLICIES',
  [APP_PATHS.HOLIDAYS]: 'HOLIDAYS',
  [APP_PATHS.REVIEW_TEMPLATES]: 'REVIEW_TEMPLATES',
  [APP_PATHS.KPIS]: 'KPIS',
  [APP_PATHS.SKILLS]: 'SKILLS',
  [APP_PATHS.SURVEY_FORMS]: 'SURVEY_FORMS',
  [APP_PATHS.CTC_LEVELS]: 'CTC_LEVELS',
  [APP_PATHS.NOC_TYPES]: 'NOC_TYPES',
  [APP_PATHS.ROLES]: 'ROLES',
  [APP_PATHS.AUDIT_LOG]: 'AUDIT_LOG',

  // The Settings overview is a page, so the trail links back to it from every settings page.
  [APP_PATHS.SETTINGS]: 'SETTINGS',
  // Not built yet (Soon pages).
  [APP_PATHS.DOCUMENTS]: 'DOCUMENTS',
  [APP_PATHS.LEAVE_RULES]: 'LEAVE_RULES',
  [APP_PATHS.WORK_SCHEDULES]: 'WORK_SCHEDULES',
  [APP_PATHS.ATTENDANCE_RULES]: 'ATTENDANCE_RULES',
  [APP_PATHS.TIMESHEET_RULES]: 'TIMESHEET_RULES',
  [APP_PATHS.REVIEW_RULES]: 'REVIEW_RULES',
  [APP_PATHS.REQUEST_TYPES]: 'REQUEST_TYPES',
  [APP_PATHS.APPROVALS]: 'APPROVALS',
  [APP_PATHS.MEAL_MENU]: 'MEAL_MENU',
  [APP_PATHS.MEAL_RULES]: 'MEAL_RULES',
};

/**
 * Pages of one record or one sub-page (react-router patterns): the breadcrumb names them after the
 * page they open from, e.g. People › Employee Details › Edit Employee.
 */
export const BREADCRUMB_SUB_PAGE_TITLES: ReadonlyArray<{ pattern: string; title: string }> = [
  { pattern: APP_PATHS.PEOPLE_NEW, title: 'ADD_EMPLOYEE' },
  { pattern: `${APP_PATHS.PEOPLE}/:employeeId`, title: 'EMPLOYEE_DETAILS' },
  { pattern: `${APP_PATHS.PEOPLE}/:employeeId/edit`, title: 'EDIT_EMPLOYEE' },
  { pattern: `${APP_PATHS.TEAM_ATTENDANCE}/:userId`, title: 'ATTENDANCE_PROFILE' },
  { pattern: `${APP_PATHS.TEAM_TIMESHEETS}/:userId`, title: 'TIMESHEET_DETAILS' },
  { pattern: `${APP_PATHS.MY_REVIEW_SUBMISSIONS}/:id`, title: 'SUBMISSION_DETAILS' },
  { pattern: `${APP_PATHS.ALL_REVIEWS}/:reviewId`, title: 'REVIEW_DETAILS' },
  { pattern: `${APP_PATHS.ALL_REVIEWS}/:reviewId/dashboard`, title: 'PERFORMANCE_DASHBOARD' },
  { pattern: APP_PATHS.CANDIDATES_NEW, title: 'PROFILE_BUILDER' },
  { pattern: APP_PATHS.OFFERS_NEW, title: 'OFFER_BUILDER' },
  { pattern: `${APP_PATHS.ALL_SURVEYS}/:batchId`, title: 'SURVEY_DETAILS' },
  { pattern: `${APP_PATHS.ALL_SURVEYS}/:batchId/:submissionId`, title: 'SURVEY_RESPONSE' },
  { pattern: `${APP_PATHS.MY_SURVEY_RESPONSES}/:submissionId`, title: 'SURVEY_RESPONSE' },
  { pattern: `${APP_PATHS.SERVICE_REQUESTS}/:type/new`, title: 'NEW_REQUEST' },
  {
    pattern: `${APP_PATHS.SERVICE_REQUESTS}/${SERVICE_REQUEST_TYPES.NOC}/:id/report`,
    title: 'NOC_CERTIFICATE',
  },
  { pattern: `${APP_PATHS.REVIEW_TEMPLATES}/:formId`, title: 'FORM_EDITOR' },
  { pattern: `${APP_PATHS.SURVEY_FORMS}/:formId`, title: 'FORM_EDITOR' },
];

/**
 * Path segments that name a menu group, not a page (`/time-off`, `/settings/organization`). The
 * breadcrumb leaves them out: it only shows crumbs that are pages.
 */
export const BREADCRUMB_GROUP_TITLES: Record<string, string> = {
  '/time-attendance': 'TIME_AND_ATTENDANCE',
  '/time-off': 'TIME_OFF',
  '/performance': 'PERFORMANCE',
  '/hiring': 'HIRING',
  '/surveys': 'SURVEYS',
  '/meal': 'MEAL',
  '/settings/organization': 'ORGANIZATION',
  '/settings/time-off': 'TIME_OFF',
  '/settings/time-attendance': 'TIME_AND_ATTENDANCE',
  '/settings/performance': 'PERFORMANCE',
  '/settings/surveys': 'SURVEYS',
  '/settings/compensation': 'COMPENSATION',
  '/settings/service-requests': 'SERVICE_REQUESTS',
  '/settings/access-approvals': 'ACCESS_AND_APPROVALS',
  '/settings/system': 'SYSTEM',
  '/settings/admin': 'ADMIN',
  '/settings/admin/meals': 'MEALS',
};
